import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRandomGameFactionOrderFaction } from '../../../constants/EntertainmentGames/gameFaction';
import { getMultiplayerGameTypeInfo } from '../../../constants/EntertainmentGames/multiplayerGame';
import { getDefaultMultiplayerGameTypeBackground } from '../../../constants/gameTheme';
import { NOT_APPLICABLE_STRING } from '../../../constants/general';
import { SpaceType } from '../../../constants/space';
import { colorForGameSetId } from '../../../constants/Theme/DefaultColors';
import i18n from '../../../i18n';
import {
  MultiplayerGame,
  MultiplayerGameAudienceType,
} from '../../../models/SpaceStore/MultiplayerGame';
import { ProfileMapType } from '../../../models/UserStore';
import { useSpaceStore } from '../../../stores/SpaceStore';
import { useUserStore } from '../../../stores/UserStore';
import { generateWinningCombination } from '../../../utils/chessGame';
import { timeAgo } from '../../../utils/date';
import { AvatarNode } from '../AvatarNode';
import { Card } from '../Card';
import { SeparatorComponent } from '../SeparatorComponent';
import { Skeleton } from '../Skeleton';
import { SpeakingContentView } from '../SpeakingContentView';
import { SvgIcon } from '../SVGIcon';

type MultiplayerGameCardProps = {
  multiplayerGame: MultiplayerGame;
  backgroundColor?: string;
};

export const MultiplayerGameCard = memo((props: MultiplayerGameCardProps) => {
  const { multiplayerGame, backgroundColor } = props;
  const [isClassroomIconAvailable, setIsClassroomIconAvailable] =
    useState(true);
  const { t } = useTranslation();

  const background = multiplayerGame?.background?.publicUrls?.small;

  const boardSize = multiplayerGame?.gameData.boardSize;

  const title =
    multiplayerGame.i18nGameInfo?.resources[
      multiplayerGame.i18nGameInfo?.defaultLocale
    ]?.title;

  const gameTypeInfo = getMultiplayerGameTypeInfo(multiplayerGame.gameType);

  const gameTypeBackground = getDefaultMultiplayerGameTypeBackground(
    multiplayerGame.gameType,
  );

  const boardDesign = multiplayerGame?.gameData.boardDesign;
  const pieceDesigns = multiplayerGame?.gameData.pieceDesigns;
  const faction = getRandomGameFactionOrderFaction(multiplayerGame.gameType);

  const classroom = useSpaceStore
    .getState()
    .getSpace(multiplayerGame.classroomId);
  const classroomOwner = useUserStore
    .getState()
    .getProfileFromMap(ProfileMapType.SpaceRoot, multiplayerGame.classroomId);

  const winCombinationSize = multiplayerGame.gameData.winCombination.length;
  const pieceSteps = generateWinningCombination(boardSize, winCombinationSize);

  return (
    <Card
      style={{
        backgroundColor: backgroundColor || '#f2f2f2',
        gap: '0.5rem',
        width: '100%',
        padding: '0rem 1rem',
        margin: ' 0 1rem',
        maxWidth: '456px',
      }}
    >
      <div
        className="flex flex-col items-center justify-center"
        style={{
          aspectRatio: '1 / 1',
          width: '90%',
          position: 'relative',
          borderRadius: '50%',
          margin: '1rem',
        }}
      >
        {/* <ColorBlendFilter blendColor={faction?.color ?? ''} /> */}
        {multiplayerGame ? (
          <>
            <SpeakingContentView
              backgroundImageSource={gameTypeBackground ?? background}
              board={{
                boardDesign: boardDesign,
                boardSize: boardSize,
              }}
              chessLayout={{
                pieceDesigns: pieceDesigns,
                pieceSteps: pieceSteps,
                iconImageSource: classroom?.icon?.publicUrls?.small,
                faction: faction,
              }}
              bubbleFrameShape={'rounded'}
              frameColor={colorForGameSetId(multiplayerGame.multiplayerGameId)}
            />
          </>
        ) : null}
      </div>
      <hr />
      <div
        className="flex flex-col gap-1"
        style={{
          justifyContent: 'start',
          width: '100%',
          padding: '0.5rem',
        }}
      >
        <p className="text-md" style={{ color: '#333333' }}>
          {title}
        </p>
        {/* {pieceDesigns ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '0.25rem',
              flexWrap: 'wrap',
            }}
          >
            {Object.entries(pieceDesigns).map(
              ([key, { icon, itemId, text }]) => {
                const item = getChessItemByJoyachessItemNo(itemId);
                const fileName = item?.visual.filePrefix;

                return (
                  <div
                    className="circle"
                    key={key}
                    style={{
                      aspectRatio: 1,
                      width: '28px',
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                      }}
                    >
                      <ColorBlendFilter blendColor={faction?.color ?? ''} />
                      <img
                        src={fileName}
                        alt="Chess Piece"
                        style={{
                          width: '100%',
                          height: '100%',
                          position: 'absolute',
                          filter: 'url(#colorBlend)',
                          mixBlendMode: 'multiply', // This blend mode works well for dark images on light backgrounds
                        }}
                      />
                    </div>

                    {icon ? (
                      <div
                        style={{
                          position: 'absolute',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          src={classroom?.icon?.publicUrls?.small}
                          style={{
                            width: '20px',
                            height: '20px',
                            border: '1px solid #cccccc50',
                            // borderRadius: '0.5rem 0.5rem 0.25rem 0.25rem',
                            borderRadius: '1rem',
                            aspectRatio: 1,
                          }}
                        />
                      </div>
                    ) : (
                      <span
                        className="text"
                        style={{
                          position: 'absolute',
                          // width: '100%',
                          // height: '100%',
                          display: 'flex',
                          color: 'white',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {text}
                      </span>
                    )}
                  </div>
                );
              },
            )}
          </div>
        ) : null} */}

        <div className="flex items-center gap-1">
          <SvgIcon
            name={gameTypeInfo.icon}
            width={24}
            height={24}
            fill={'#808080'}
            stroke={'#808080'}
          />

          <p className="text-gray-600 text-sm">{t(gameTypeInfo.name)}</p>
        </div>
        <div className="flex items-center gap-1" style={{ flexWrap: 'wrap' }}>
          <SeparatorComponent
            separator={<p className="text-gray-600 text-sm">·</p>}
          >
            <div className="flex flex-row items-center gap-1">
              {classroomOwner && classroom?.type == SpaceType.Home ? (
                <>
                  <AvatarNode
                    avatar={{
                      ...classroomOwner.avatar,
                      userId: classroomOwner.userId,
                      source:
                        classroomOwner.profilePicture &&
                        classroomOwner.profilePicture.publicUrls?.small
                          ? {
                              uri: classroomOwner.profilePicture.publicUrls
                                .small,
                            }
                          : NOT_APPLICABLE_STRING,
                    }}
                    size={32}
                  />
                </>
              ) : classroom?.icon?.publicUrls?.small &&
                isClassroomIconAvailable ? (
                <img
                  src={classroom?.icon?.publicUrls?.small}
                  onError={() => setIsClassroomIconAvailable(false)}
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '1px solid #cccccc50',
                    // borderRadius: '0.5rem 0.5rem 0.25rem 0.25rem',
                    borderRadius: '1rem',
                    aspectRatio: 1,
                  }}
                />
              ) : (
                <Skeleton
                  width={'32px'}
                  height={'32px'}
                  // borderRadius={'0.5rem 0.5rem 0.25rem 0.25rem'}
                  borderRadius={'1rem'}
                />
              )}

              <p className="text-gray-600 text-sm">
                {
                  classroom?.i18nClassroomInfo.resources[
                    classroom.i18nClassroomInfo.defaultLocale
                  ]?.name
                }
              </p>
            </div>

            {multiplayerGame ? (
              <p className="text-gray-600 text-sm">
                {timeAgo(multiplayerGame.createdAt, i18n.language)}
              </p>
            ) : null}

            {multiplayerGame.audience ? (
              <SvgIcon
                name={
                  multiplayerGame.audience == MultiplayerGameAudienceType.Public
                    ? 'Region'
                    : multiplayerGame.audience ==
                      MultiplayerGameAudienceType.Members
                    ? 'Member'
                    : multiplayerGame.audience ==
                      MultiplayerGameAudienceType.Followers
                    ? 'Friend'
                    : 'MembershipTier'
                }
                width={20}
                height={20}
                stroke="#808080"
              />
            ) : null}

            {/* {multiplayerGame &&
            multiplayerGame.statistics.gamePlayCount &&
            album.statistics.gamePlayCount > 0 ? (
              <p className="text-gray-600 text-sm">
                {t('{count} Plays', {
                  count: album.statistics.gamePlayCount,
                })}
              </p>
            ) : null} */}
          </SeparatorComponent>
        </div>
      </div>
    </Card>
  );
});

//
